import type { User } from '@supabase/supabase-js'
import type { AuthenticatedUser } from '../../types/domain'
import { profileSchema } from '../../validation/schemas'
import { supabase } from './client'
import { cacheProfile, getCachedProfile } from '../offline/cache'
import { defaultPermissionsForRole, isPermissionKey } from '../../features/auth/permissions'

async function loadProfile(user: User): Promise<AuthenticatedUser> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .eq('ativo', true)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      await supabase.auth.signOut({ scope: 'local' })
      throw new Error('Seu acesso está inativo. Procure um administrador.')
    }
    const cached = await getCachedProfile(user.id)
    if (cached && cached.email === user.email) return cached
    throw new Error('Usuário autenticado sem perfil ativo autorizado.')
  }
  const profile = profileSchema.parse(data)
  if (!user.email) throw new Error('A sessão autenticada não possui e-mail.')
  const permissionsResult = await supabase.rpc('get_current_user_permissions')
  if (permissionsResult.error) throw new Error('Não foi possível validar suas permissões efetivas.')
  const permissions = Array.isArray(permissionsResult.data)
    ? permissionsResult.data
      .map((entry) => typeof entry === 'string' ? entry : entry?.permission_key)
      .filter(isPermissionKey)
    : defaultPermissionsForRole(profile.role)
  const authenticated = { userId: user.id, email: user.email, profile, permissions }
  await cacheProfile(authenticated)
  return authenticated
}

export const authService = {
  async signInWithPassword(email: string, password: string): Promise<AuthenticatedUser> {
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    if (error || !data.user) {
      if (error && /fetch|network|abort|timeout|temporar/i.test(error.message)) {
        throw new Error('Serviço temporariamente indisponível. Tente novamente em instantes.')
      }
      throw new Error('Não foi possível autenticar. Confira e-mail e senha.')
    }
    return loadProfile(data.user)
  },

  async recoverSession(): Promise<AuthenticatedUser | null> {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw new Error('Não foi possível recuperar a sessão segura.')
    return data.session?.user ? loadProfile(data.session.user) : null
  },

  onAuthStateChange(callback: () => void): () => void {
    const { data } = supabase.auth.onAuthStateChange(() => callback())
    return () => data.subscription.unsubscribe()
  },

  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut({ scope: 'local' })
    if (error) throw new Error('Não foi possível encerrar a sessão.')
  },

  async updateOwnName(nome: string): Promise<AuthenticatedUser> {
    const { error } = await supabase.rpc('update_own_profile', { p_nome: nome.trim() })
    if (error) throw new Error('Não foi possível atualizar seus dados.')
    const { data } = await supabase.auth.getUser()
    if (!data.user) throw new Error('Sua sessão expirou.')
    return loadProfile(data.user)
  },

  async changeOwnPassword(currentPassword: string, newPassword: string): Promise<void> {
    const current = await supabase.auth.getUser()
    if (current.error || !current.data.user?.email) throw new Error('Sua sessão expirou.')
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: current.data.user.email,
      password: currentPassword,
    })
    if (signInError || data.user?.id !== current.data.user.id) {
      throw new Error('A senha atual está incorreta.')
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw new Error('Não foi possível definir a nova senha.')
  },
}
